import React, { useMemo, useState } from 'react'
import {
  Bar,
  CartesianGrid,
  ComposedChart,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts'
import { format, parseISO } from 'date-fns'
import { Loadable, varClass } from '../../shared'
import {
  IAnomaliesV2TimelineResponse,
  IAnomaliesV2TimelineRow,
} from '../../shared/types'
import { FAMILY_COLORS, tickFormatterFor } from '../shared'

// Each view picks a single series to highlight, except "all" which stacks
// the family breakdown + overlays new alerts. Default is "all".
type View = 'all' | 'new' | 'active' | 'drift' | 'acute'

const VIEW_LABELS: Record<View, string> = {
  all: 'All',
  new: 'New',
  active: 'Active',
  drift: 'Drift',
  acute: 'Acute',
}

const xTickFormatter = (s: string) => {
  try {
    return format(parseISO(s), 'MMM d')
  } catch {
    return s
  }
}

export const TimelineChart = (props: {
  data: IAnomaliesV2TimelineResponse | null
  selectedDate?: string
  onSelectDate?: (date: string) => void
}) => {
  const { data, selectedDate, onSelectDate } = props
  const [view, setView] = useState<View>('all')

  const rows = data?.rows || []

  const chartData = useMemo(
    () =>
      rows.map((r: IAnomaliesV2TimelineRow) => ({
        date: r.eventDate,
        active: r.activeAlerts,
        suppressed: r.suppressedAlerts ?? 0,
        newAlerts: r.newAlerts,
        worsening: r.worseningAlerts,
        drift: r.driftAlerts,
        acute: r.acuteAlerts,
      })),
    [rows]
  )

  // Show a faint suppressed-alerts overlay only when there's something to
  // show — keeps the chart clean in the common case where no advertiser is
  // currently suppressed.
  const hasSuppressed = chartData.some((d) => d.suppressed > 0)

  const handleClick = (entry: any) => {
    if (!onSelectDate) return
    const date = entry?.activeLabel
    if (typeof date === 'string') onSelectDate(date)
  }

  return (
    <div className="card anomalies-trend-chart">
      <div className="card-body p-y-20">
        <div className="row row-space-between row-center m-b-3">
          <div>
            <div className="text-dark text-bolder">Anomaly trend</div>
            <div className="text-light text-small m-t-05">
              Daily alert volume. Click a bar to filter the table to that day.
            </div>
          </div>
          <div className="anomalies-trend-toggle-group">
            {(Object.keys(VIEW_LABELS) as View[]).map((v) => (
              <button
                key={v}
                type="button"
                className={varClass({
                  'anomalies-trend-toggle': true,
                  'active': view === v,
                })}
                onClick={() => setView(v)}
              >
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
        </div>

        <Loadable data={data}>
          {chartData.length === 0 ? (
            <div className="text-center text-light p-y-10">
              No alerts in this range.
            </div>
          ) : (
            <ResponsiveContainer width="99.9%" height={180}>
              <ComposedChart
                data={chartData}
                margin={{ top: 8, left: 0, right: 0, bottom: 0 }}
                onClick={handleClick}
              >
                <CartesianGrid
                  horizontal
                  vertical={false}
                  strokeDasharray="6"
                  stroke="#EDEFF2"
                />
                {selectedDate && (
                  <ReferenceLine
                    x={selectedDate}
                    stroke="rgba(79, 158, 236, 0.7)"
                    strokeWidth={2}
                  />
                )}
                <Tooltip content={<TimelineTooltip />} />

                {/* "all" — stacked family bars + new-alerts line overlay. */}
                {view === 'all' && (
                  <Bar
                    dataKey="drift"
                    stackId="family"
                    fill={FAMILY_COLORS.drift}
                    cursor="pointer"
                  />
                )}
                {view === 'all' && (
                  <Bar
                    dataKey="acute"
                    stackId="family"
                    fill={FAMILY_COLORS.acute}
                    cursor="pointer"
                    radius={[3, 3, 0, 0]}
                  />
                )}
                {view === 'all' && (
                  <Line
                    dataKey="newAlerts"
                    stroke="#1F8A4A"
                    strokeWidth={2}
                    dot={false}
                    isAnimationActive={false}
                  />
                )}

                {/* Single-series views — one bar at full height. */}
                {view === 'new' && (
                  <Bar
                    dataKey="newAlerts"
                    fill="#1F8A4A"
                    cursor="pointer"
                    radius={[3, 3, 0, 0]}
                  />
                )}
                {view === 'active' && (
                  <Bar
                    dataKey="active"
                    fill="#4F9EEC"
                    cursor="pointer"
                    radius={[3, 3, 0, 0]}
                  />
                )}
                {view === 'drift' && (
                  <Bar
                    dataKey="drift"
                    fill={FAMILY_COLORS.drift}
                    cursor="pointer"
                    radius={[3, 3, 0, 0]}
                  />
                )}
                {view === 'acute' && (
                  <Bar
                    dataKey="acute"
                    fill={FAMILY_COLORS.acute}
                    cursor="pointer"
                    radius={[3, 3, 0, 0]}
                  />
                )}

                {/* Faint suppressed-alerts line overlay across all views, so
                    operators can see how much is currently being hidden. */}
                {hasSuppressed && (
                  <Line
                    dataKey="suppressed"
                    stroke="#828B9E"
                    strokeWidth={1.5}
                    strokeDasharray="4 3"
                    dot={false}
                    isAnimationActive={false}
                  />
                )}

                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  interval="preserveStartEnd"
                  tickFormatter={xTickFormatter}
                  tick={{ fontSize: 11, fill: '#828B9E' }}
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  width={32}
                  allowDecimals={false}
                  tickFormatter={tickFormatterFor('number')}
                  tick={{ fontSize: 11, fill: '#828B9E' }}
                />
              </ComposedChart>
            </ResponsiveContainer>
          )}
        </Loadable>
      </div>
    </div>
  )
}

const TimelineTooltip = (props: any) => {
  const { active, payload, label } = props
  if (!active || !payload || !payload.length) return null
  const row = payload[0]?.payload
  if (!row) return null

  return (
    <div className="anomalies-chart-tooltip">
      <div className="anomalies-chart-tooltip-date">
        {label ? format(parseISO(label), 'MMM d, yyyy') : ''}
      </div>
      <Row label="Active" value={row.active} color="#4F9EEC" />
      <Row label="New" value={row.newAlerts} color="#1F8A4A" />
      <Row label="Worsening" value={row.worsening} color="#EBB617" />
      <Row label="Drift" value={row.drift} color={FAMILY_COLORS.drift} />
      <Row label="Acute" value={row.acute} color={FAMILY_COLORS.acute} />
      {row.suppressed > 0 && (
        <Row label="Suppressed" value={row.suppressed} color="#828B9E" />
      )}
    </div>
  )
}

const Row = (props: { label: string; value: number; color: string }) => (
  <div className="anomalies-chart-tooltip-row">
    <span
      className="anomalies-chart-tooltip-dot"
      style={{ background: props.color }}
    />
    <span className="anomalies-chart-tooltip-label">{props.label}</span>
    <span className="anomalies-chart-tooltip-value">{props.value ?? 0}</span>
  </div>
)
